import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import AppLayout from "@/components/AppLayout";
import { Loader2, TrendingUp, Flame, Clock, Calendar } from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";

const ProgressPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState<any[]>([]);
  const [visualizations, setVisualizations] = useState<any[]>([]);
  const [stats, setStats] = useState({
    totalSessions: 0,
    totalMinutes: 0,
    currentStreak: 0,
    avgPerformanceLift: 0,
  });

  useEffect(() => {
    const load = async () => {
      if (!user) return;

      const [{ data: sessionsData }, { data: vizData }] = await Promise.all([
        supabase
          .from("completed_sessions")
          .select("*")
          .eq("user_id", user.id)
          .order("completed_at", { ascending: true }),
        supabase
          .from("visualizations")
          .select("*")
          .eq("user_id", user.id)
          .eq("generation_status", "completed")
          .order("created_at", { ascending: true }),
      ]);

      const allSessions = sessionsData || [];
      const allViz = vizData || [];

      setSessions(allSessions);
      setVisualizations(allViz);

      // Calculate stats
      const totalMinutes = allSessions.reduce(
        (acc, s) => acc + (s.duration_seconds || 0) / 60,
        0
      );

      const lifts = allSessions
        .filter((s) => s.mental_performance_lift != null)
        .map((s) => Number(s.mental_performance_lift));
      const avgLift = lifts.length > 0 ? lifts.reduce((a, b) => a + b, 0) / lifts.length : 0;

      // Calculate streak
      const streak = calculateStreak(allViz);

      setStats({
        totalSessions: allSessions.length,
        totalMinutes: Math.round(totalMinutes),
        currentStreak: streak,
        avgPerformanceLift: Math.round(avgLift * 10) / 10,
      });

      setLoading(false);
    };
    load();
  }, [user]);

  const calculateStreak = (vizList: any[]) => {
    if (vizList.length === 0) return 0;

    const days = new Set(
      vizList.map((v) => new Date(v.created_at).toISOString().split("T")[0])
    );
    const sortedDays = Array.from(days).sort().reverse();

    const today = new Date().toISOString().split("T")[0];
    const yesterday = new Date(Date.now() - 86400000).toISOString().split("T")[0];

    if (!days.has(today) && !days.has(yesterday)) return 0;

    let streak = 0;
    let checkDate = new Date(days.has(today) ? today : yesterday);
    while (days.has(checkDate.toISOString().split("T")[0])) {
      streak++;
      checkDate = new Date(checkDate.getTime() - 86400000);
    }
    return streak;
  };

  // Build chart data: sessions per week
  const buildWeeklyData = () => {
    if (visualizations.length === 0) return [];
    const weekMap: Record<string, { sessions: number; minutes: number }> = {};
    
    visualizations.forEach((v) => {
      const date = new Date(v.created_at);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const key = weekStart.toISOString().split("T")[0];
      if (!weekMap[key]) weekMap[key] = { sessions: 0, minutes: 0 };
      weekMap[key].sessions++;
      weekMap[key].minutes += (v.audio_duration_seconds || 0) / 60;
    });

    return Object.entries(weekMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-12)
      .map(([week, data]) => ({
        week: new Date(week).toLocaleDateString("en", { month: "short", day: "numeric" }),
        sessions: data.sessions,
        minutes: Math.round(data.minutes),
      }));
  };

  // Build type breakdown
  const buildTypeData = () => {
    const types: Record<string, number> = {};
    visualizations.forEach((v) => {
      types[v.visualization_type] = (types[v.visualization_type] || 0) + 1;
    });
    return Object.entries(types).map(([type, count]) => ({
      type: type.charAt(0).toUpperCase() + type.slice(1),
      count,
    }));
  };

  // Build mental performance lift trend
  const buildLiftTrend = () => {
    if (sessions.length === 0) return [];
    return sessions
      .filter((s) => s.mental_performance_lift != null)
      .slice(-20)
      .map((s, i) => ({
        session: i + 1,
        lift: Number(s.mental_performance_lift),
        pre: s.pre_mental_performance,
        post: s.post_mental_performance,
        date: new Date(s.completed_at).toLocaleDateString("en", { month: "short", day: "numeric" }),
      }));
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  const weeklyData = buildWeeklyData();
  const typeData = buildTypeData();
  const liftTrend = buildLiftTrend();

  return (
    <AppLayout>
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold mb-2">Your Progress</h1>
        <p className="text-muted-foreground">Track your mental training journey.</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4 mb-10">
        {[
          { icon: Calendar, label: "Total Sessions", value: stats.totalSessions },
          { icon: Clock, label: "Total Minutes", value: stats.totalMinutes },
          { icon: Flame, label: "Current Streak", value: `${stats.currentStreak} day${stats.currentStreak !== 1 ? "s" : ""}` },
          { icon: TrendingUp, label: "Avg. Performance Lift", value: stats.avgPerformanceLift > 0 ? `+${stats.avgPerformanceLift}` : "—" },
        ].map((stat) => (
          <div
            key={stat.label}
            className="gradient-card rounded-xl border border-border/50 p-5 shadow-card"
          >
            <stat.icon className="w-5 h-5 text-primary mb-3" />
            <div className="font-display text-2xl font-bold">{stat.value}</div>
            <div className="text-xs text-muted-foreground mt-1">{stat.label}</div>
          </div>
        ))}
      </div>

      {visualizations.length === 0 ? (
        <div className="text-center py-16 gradient-card rounded-xl border border-border/50">
          <p className="text-muted-foreground">Complete your first session to see progress data here.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Weekly sessions chart */}
          {weeklyData.length > 0 && (
            <div className="gradient-card rounded-xl border border-border/50 p-6 shadow-card">
              <h2 className="font-display text-lg font-semibold mb-6">Sessions Per Week</h2>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={weeklyData}>
                  <defs>
                    <linearGradient id="sessionsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(185 72% 48%)" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="hsl(185 72% 48%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 15% 18%)" />
                  <XAxis dataKey="week" tick={{ fontSize: 12, fill: "hsl(215 12% 55%)" }} />
                  <YAxis tick={{ fontSize: 12, fill: "hsl(215 12% 55%)" }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(220 20% 10%)",
                      border: "1px solid hsl(220 15% 18%)",
                      borderRadius: "8px",
                      color: "hsl(210 20% 95%)",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="sessions"
                    stroke="hsl(185 72% 48%)"
                    fill="url(#sessionsGrad)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Mental performance lift trend */}
          {liftTrend.length > 0 && (
            <div className="gradient-card rounded-xl border border-border/50 p-6 shadow-card">
              <h2 className="font-display text-lg font-semibold mb-6">Mental Performance Trend</h2>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={liftTrend}>
                  <defs>
                    <linearGradient id="liftGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(142 72% 48%)" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="hsl(142 72% 48%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 15% 18%)" />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: "hsl(215 12% 55%)" }} />
                  <YAxis tick={{ fontSize: 12, fill: "hsl(215 12% 55%)" }} />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(220 20% 10%)",
                      border: "1px solid hsl(220 15% 18%)",
                      borderRadius: "8px",
                      color: "hsl(210 20% 95%)",
                    }}
                    formatter={(value: number, name: string) => {
                      const labels: Record<string, string> = { lift: "Lift", pre: "Before", post: "After" };
                      return [value, labels[name] || name];
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="pre"
                    stroke="hsl(215 12% 55%)"
                    fill="none"
                    strokeWidth={1}
                    strokeDasharray="4 4"
                  />
                  <Area
                    type="monotone"
                    dataKey="post"
                    stroke="hsl(185 72% 48%)"
                    fill="none"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="lift"
                    stroke="hsl(142 72% 48%)"
                    fill="url(#liftGrad)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
              <div className="flex items-center gap-6 mt-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[hsl(215_12%_55%)] inline-block" style={{ borderTop: "1px dashed" }} /> Before</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[hsl(185_72%_48%)] inline-block" /> After</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[hsl(142_72%_48%)] inline-block" /> Lift</span>
              </div>
            </div>
          )}

          {/* Type breakdown */}
          {typeData.length > 0 && (
            <div className="gradient-card rounded-xl border border-border/50 p-6 shadow-card">
              <h2 className="font-display text-lg font-semibold mb-6">Session Types</h2>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={typeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 15% 18%)" />
                  <XAxis dataKey="type" tick={{ fontSize: 12, fill: "hsl(215 12% 55%)" }} />
                  <YAxis tick={{ fontSize: 12, fill: "hsl(215 12% 55%)" }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(220 20% 10%)",
                      border: "1px solid hsl(220 15% 18%)",
                      borderRadius: "8px",
                      color: "hsl(210 20% 95%)",
                    }}
                  />
                  <Bar dataKey="count" fill="hsl(185 72% 48%)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Recent sessions */}
          <div className="gradient-card rounded-xl border border-border/50 p-6 shadow-card">
            <h2 className="font-display text-lg font-semibold mb-4">Recent Visualizations</h2>
            <div className="space-y-3">
              {visualizations.slice(-10).reverse().map((v) => (
                <div
                  key={v.id}
                  className="flex items-center justify-between py-2 border-b border-border/30 last:border-0"
                >
                  <div>
                    <span className="text-sm font-medium">{v.title || "Untitled"}</span>
                    <span className="text-xs text-muted-foreground ml-3 capitalize">
                      {v.visualization_type}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(v.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default ProgressPage;
